import { motion } from "framer-motion";
import { ARTIST, PORTRAIT_EDITORIAL_URL } from "@/lib/constants";
import { fadeUp, stagger, viewportOnce } from "@/lib/motion";

const paragraphs = ARTIST.bio.split("\n\n");

export const About = () => {
  return (
    <section
      id="about"
      data-testid="about-section"
      className="relative w-full bg-bg py-24 md:py-32 border-t border-[color:var(--line)] overflow-hidden"
    >
      <div className="mx-auto max-w-[1500px] px-6 md:px-10">
        <motion.div
          variants={stagger(0.1, 0.05)}
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          className="mb-14 max-w-3xl"
        >
          <motion.p
            variants={fadeUp}
            className="mb-3 inline-flex items-center gap-3 text-[10px] font-semibold tracking-[0.32em] uppercase text-gold"
          >
            <span className="h-px w-8 bg-[color:var(--gold)]/70" />
            Chapter 09 — Biography
          </motion.p>
          <motion.h2
            variants={fadeUp}
            data-testid="about-title"
            className="font-display text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-ink"
          >
            The <span className="font-editorial text-gold-hi">artist</span> behind
            the sound.
          </motion.h2>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          {/* Portrait */}
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-5"
          >
            <div className="relative">
              <div className="absolute -inset-3 border border-[color:var(--line)] pointer-events-none" />
              <div className="relative overflow-hidden">
                <img
                  src={PORTRAIT_EDITORIAL_URL}
                  alt="Irwan Craw — editorial portrait"
                  className="w-full h-auto object-cover"
                  style={{ filter: "contrast(1.04) saturate(1.02)" }}
                  data-testid="about-portrait"
                />
                {/* Warm gold rim light on left edge */}
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(95deg, rgba(229,204,148,0.16) 0%, rgba(201,169,97,0.05) 8%, transparent 22%)",
                    mixBlendMode: "screen",
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.55) 100%)",
                  }}
                />
                <div
                  aria-hidden
                  className="pointer-events-none absolute inset-0 opacity-[0.12] mix-blend-overlay"
                  style={{
                    backgroundImage:
                      "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/></svg>\")",
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 p-5 flex items-end justify-between">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.32em] text-ink/80">
                    <span className="text-gold">Portrait</span> · MMXXV
                  </p>
                  <span className="font-serif-signature text-lg text-[color:var(--gold-hi)]/90">
                    Irwan Craw
                  </span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bio */}
          <motion.div
            variants={stagger(0.14, 0.05)}
            initial="hidden"
            whileInView="visible"
            viewport={viewportOnce}
            className="lg:col-span-7"
          >
            {paragraphs.map((p, i) => (
              <motion.p
                key={i}
                variants={fadeUp}
                data-testid={`about-paragraph-${i}`}
                className={
                  i === 0
                    ? "font-editorial text-2xl md:text-3xl text-ink/90 leading-snug"
                    : "mt-6 text-sm md:text-base text-muted leading-relaxed"
                }
              >
                {p}
              </motion.p>
            ))}

            {/* Keyword accent list */}
            <motion.div
              variants={fadeUp}
              className="mt-10 flex flex-wrap gap-2"
            >
              {[
                "Afro House",
                "Deep House",
                "Melodic House",
                "Latin House",
                "Ibiza",
                "Tulum",
                "Mykonos",
                "Burning Man",
              ].map((k) => (
                <span
                  key={k}
                  className="inline-flex items-center rounded-full border border-[color:var(--line-strong)] px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-ink/80"
                >
                  {k}
                </span>
              ))}
            </motion.div>

            {/* Stat inline */}
            <motion.div
              variants={fadeUp}
              className="mt-10 grid grid-cols-3 gap-6 border-t border-[color:var(--line)] pt-8"
            >
              <div>
                <p className="font-display text-3xl md:text-4xl font-black text-shine">
                  FR
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted">
                  Based
                </p>
              </div>
              <div>
                <p className="font-display text-3xl md:text-4xl font-black text-shine">
                  MMXXV
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted">
                  Current Era
                </p>
              </div>
              <div>
                <p className="font-display text-3xl md:text-4xl font-black text-shine">
                  ∞
                </p>
                <p className="mt-1 text-[10px] font-semibold uppercase tracking-[0.28em] text-muted">
                  Energy
                </p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
